/**
 * spiceRosterShape — the pure, unit-tested layer behind the all-clients
 * SPICE view (/clients/all?view=spice): the last-session/this-month copy
 * and the roster's default sort order. No fetches, no React. The
 * schedule cell's words and the hold chip come from the shared
 * formatters (src/lib/spiceScheduleCopy.ts).
 */

import { formatSpiceShortDate } from './spiceScheduleCopy';

/** What the sort reads off a row's schedule: its sessions' days and times. */
export interface SpiceRosterSchedule {
  sessions: readonly { weekday: number; startTime: string }[];
}

/**
 * "Last session 31 Aug · declined 3 Sep" — sessions and declines read
 * apart; a decline is appended, never a substitute for the last time a
 * visit actually happened. "No session yet" when there has never been a
 * real session, "· declined {date}" appended only when a decline is on
 * file at all (order in the string is fixed — session first — regardless
 * of which date is more recent).
 */
export function formatSpiceLastSession(lastSession: string | null, lastDeclined: string | null): string {
  const sessionPart = lastSession ? `Last session ${formatSpiceShortDate(lastSession)}` : 'No session yet';
  return lastDeclined ? `${sessionPart} · declined ${formatSpiceShortDate(lastDeclined)}` : sessionPart;
}

/**
 * "No sessions yet this month" / "1 session this month" / "3 sessions ·
 * 1 decline this month": the decline clause appears only when there is
 * at least one. A month with declines and no session reads "No sessions
 * yet this month · 1 decline".
 */
export function formatSpiceThisMonth(sessionsThisMonth: number, declinesThisMonth: number): string {
  const declinesPart =
    declinesThisMonth > 0 ? ` · ${declinesThisMonth} decline${declinesThisMonth === 1 ? '' : 's'}` : '';
  if (sessionsThisMonth === 0) return `No sessions yet this month${declinesPart}`;
  const sessionsPart = `${sessionsThisMonth} session${sessionsThisMonth === 1 ? '' : 's'}`;
  return `${sessionsPart}${declinesPart} this month`;
}

// ---------------------------------------------------------------------
// Default sort: therapist, then weekday and time. "Sort by client" and
// "sort by last session" are the two alternates the filter bar offers.
// ---------------------------------------------------------------------

export type SpiceRosterSortMode = 'therapist' | 'client' | 'last_session';

export interface SpiceRosterSortableRow {
  display_name: string;
  therapist_name: string | null;
  last_session_occurred_on: string | null;
  schedule: SpiceRosterSchedule | null;
}

/** The row's earliest session, as a sortable (weekday, time) tuple. A
 *  row with no schedule (or no sessions) sorts AFTER every scheduled row
 *  in its group: a blank schedule carries no time to sort by. */
function earliestSlotKey(schedule: SpiceRosterSchedule | null): readonly [number, string] {
  if (schedule === null || schedule.sessions.length === 0) return [99, '99:99'];
  const first = [...schedule.sessions].sort((a, b) =>
    a.weekday !== b.weekday ? a.weekday - b.weekday : a.startTime.localeCompare(b.startTime),
  )[0]!;
  return [first.weekday, first.startTime];
}

/**
 * Comparator for Array#sort. 'therapist' (the default): therapist name
 * A–Z (a client with no assigned therapist sorts last), then weekday and
 * time within the same therapist. 'client': display name A–Z.
 * 'last_session': most recent visit first; a client never seen sorts
 * last.
 */
export function compareSpiceRosterRows(
  a: SpiceRosterSortableRow,
  b: SpiceRosterSortableRow,
  mode: SpiceRosterSortMode,
): number {
  if (mode === 'client') return a.display_name.localeCompare(b.display_name);

  if (mode === 'last_session') {
    if (a.last_session_occurred_on === null && b.last_session_occurred_on === null) {
      return a.display_name.localeCompare(b.display_name);
    }
    if (a.last_session_occurred_on === null) return 1;
    if (b.last_session_occurred_on === null) return -1;
    if (a.last_session_occurred_on !== b.last_session_occurred_on) {
      return b.last_session_occurred_on.localeCompare(a.last_session_occurred_on);
    }
    return a.display_name.localeCompare(b.display_name);
  }

  // 'therapist' (default): name asc, unassigned last, then weekday/time.
  if (a.therapist_name === null && b.therapist_name !== null) return 1;
  if (a.therapist_name !== null && b.therapist_name === null) return -1;
  if (a.therapist_name !== null && b.therapist_name !== null) {
    const nameCmp = a.therapist_name.localeCompare(b.therapist_name);
    if (nameCmp !== 0) return nameCmp;
  }
  const [aw, at] = earliestSlotKey(a.schedule);
  const [bw, bt] = earliestSlotKey(b.schedule);
  if (aw !== bw) return aw - bw;
  if (at !== bt) return at.localeCompare(bt);
  return a.display_name.localeCompare(b.display_name);
}
